(function (root, factory) {
  var api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.OotbSubscriptionGifts = api;
  api.init();
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var GIFT_PROPERTY = '_ootb_subscription_gift';
  var GIFT_PROPERTY_VALUE = 'first-subscription-order';
  var CART_MUTATION_PATTERN = /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?(?:[?#]|$)/;

  function asString(value) {
    return value === undefined || value === null ? '' : String(value);
  }

  function uniqueStrings(values) {
    return Array.from(new Set((values || []).map(asString).filter(Boolean)));
  }

  function isOurGift(item) {
    return Boolean(item && item.properties && item.properties[GIFT_PROPERTY]);
  }

  function isAnyGift(item) {
    var properties = item && item.properties;
    return Boolean(
      isOurGift(item) ||
      (properties && (properties.__cd_opus_free_gift || properties._EgoFreeGift || properties._opus_reward_prd))
    );
  }

  function sellingPlan(item) {
    return item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
  }

  function sellingPlanId(item) {
    var allocation = item && item.selling_plan_allocation;
    var plan = sellingPlan(item);
    return asString((plan && plan.id) || (allocation && allocation.selling_plan_id));
  }

  function isEligibleSubscription(item, config) {
    if (!item || isAnyGift(item) || !item.selling_plan_allocation) return false;
    return uniqueStrings(config && config.eligibleSellingPlanIds).includes(sellingPlanId(item));
  }

  function desiredGiftIds(config) {
    if (!config || config.enabled !== true) return [];

    return uniqueStrings(
      (config.gifts || [])
        .filter(function (gift) {
          return gift && gift.available === true;
        })
        .map(function (gift) {
          return gift.variantId;
        })
    );
  }

  function planReconciliation(cart, config) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var qualifying = items.some(function (item) {
      return isEligibleSubscription(item, config);
    });
    var desired = qualifying ? desiredGiftIds(config) : [];
    var desiredSet = new Set(desired);
    var kept = new Set();
    var updates = {};

    items.forEach(function (item) {
      if (!isOurGift(item)) return;

      var variantId = asString(item.variant_id || item.id);
      var isValid = desiredSet.has(variantId) && !item.selling_plan_allocation && !kept.has(variantId);
      if (!isValid) {
        if (item.key) updates[item.key] = 0;
        return;
      }

      kept.add(variantId);
      if (Number(item.quantity) !== 1 && item.key) updates[item.key] = 1;
    });

    return {
      qualifies: qualifying,
      updates: updates,
      additions: desired.filter(function (variantId) {
        return !kept.has(variantId);
      })
    };
  }

  function createGiftItems(variantIds) {
    return variantIds.map(function (variantId) {
      var properties = {};
      properties[GIFT_PROPERTY] = GIFT_PROPERTY_VALUE;
      return {
        id: Number(variantId),
        quantity: 1,
        properties: properties
      };
    });
  }

  async function executeReconciliation(options) {
    var config = options.config || {};
    var transport = options.transport;
    var state = await transport.getCart();
    var plan = planReconciliation(state, config);
    var changed = false;

    if (Object.keys(plan.updates).length) {
      state = await transport.updateLines(plan.updates);
      changed = true;
      plan = planReconciliation(state, config);
    }

    if (plan.additions.length) {
      await transport.addItems(createGiftItems(plan.additions));
      state = await transport.getCart();
      changed = true;
    }

    if (changed && typeof transport.sync === 'function') transport.sync(state);

    return { cart: state, changed: changed };
  }

  function createSerialScheduler(run, onError) {
    var tail = Promise.resolve();
    var pendingCart = null;
    var pendingReason = '';
    var queued = false;

    function schedule(cart, reason) {
      if (cart && Array.isArray(cart.items)) pendingCart = cart;
      pendingReason = reason || pendingReason;
      if (queued) return tail;

      queued = true;
      tail = tail.catch(function () {}).then(async function () {
        queued = false;
        var nextCart = pendingCart;
        var nextReason = pendingReason;
        pendingCart = null;
        pendingReason = '';

        try {
          return { ok: true, value: await run(nextCart, nextReason) };
        } catch (error) {
          if (typeof onError === 'function') onError(error, nextReason);
          return { ok: false, error: error };
        }
      });

      return tail;
    }

    return { schedule: schedule };
  }

  function cartPath(input) {
    var value = typeof input === 'string' ? input : input && input.url;
    if (!value) return '';

    try {
      return new URL(value, root.location && root.location.href).pathname;
    } catch (error) {
      return value;
    }
  }

  function isCartMutation(input) {
    return CART_MUTATION_PATTERN.test(cartPath(input));
  }

  function checkoutTarget(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.find(function (node) {
      return node && typeof node.matches === 'function' && node.matches('.cd-checkout-section-button, a[href*="/checkout"]');
    });
  }

  function createRuntime(config) {
    var originalFetch = root.fetch.bind(root);
    var checkoutReplay = false;
    var suppressCartEvent = false;
    var rootUrl = asString(config.rootUrl || (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/');
    if (!rootUrl.endsWith('/')) rootUrl += '/';

    async function request(path, options) {
      var response = await originalFetch(rootUrl + path, options);
      var payload;

      try {
        payload = await response.json();
      } catch (error) {
        throw new Error('Subscription gift cart response was not valid JSON');
      }

      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Subscription gift cart request failed');
      }

      return payload;
    }

    function sync(state) {
      if (typeof root.OpusSetCartData === 'function') {
        root.OpusSetCartData(state);
      } else if (typeof root.opusRefreshCart === 'function') {
        root.opusRefreshCart();
      }

      if (!root.document) return;
      suppressCartEvent = true;
      root.document.dispatchEvent(new root.CustomEvent('cart:updated', { detail: { cart: state } }));
      suppressCartEvent = false;
    }

    var transport = {
      getCart: function () {
        return request('cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      },
      updateLines: function (updates) {
        return request('cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      },
      addItems: function (items) {
        return request('cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      },
      sync: sync
    };

    function report(error, reason) {
      var detail = {
        reason: reason || 'unknown',
        message: error && error.message ? error.message : String(error)
      };
      if (root.console && typeof root.console.error === 'function') {
        root.console.error('[Subscription gifts]', detail.message);
      }
      if (root.document) {
        root.document.dispatchEvent(new root.CustomEvent('ootb:subscription-gift-error', { detail: detail }));
      }
    }

    var scheduler = createSerialScheduler(function (cart) {
      return executeReconciliation({ cart: cart, config: config, transport: transport });
    }, report);

    function observeFetch() {
      root.fetch = function () {
        var args = arguments;
        var mutation = isCartMutation(args[0]);
        var responsePromise = originalFetch.apply(this, args);
        if (!mutation) return responsePromise;

        return responsePromise.then(function (response) {
          if (response && response.ok) scheduler.schedule(null, 'cart-fetch');
          return response;
        });
      };
    }

    function observeCartEvents() {
      if (!root.document) return;
      root.document.addEventListener('cart:updated', function (event) {
        if (suppressCartEvent) return;
        scheduler.schedule(event.detail && event.detail.cart, 'cart-event');
      });
    }

    function observeXhr() {
      if (typeof root.XMLHttpRequest !== 'function') return;
      var prototype = root.XMLHttpRequest.prototype;
      var originalOpen = prototype.open;
      var originalSend = prototype.send;
      var mutations = new WeakMap();

      prototype.open = function (method, url) {
        mutations.set(this, isCartMutation(url));
        return originalOpen.apply(this, arguments);
      };

      prototype.send = function () {
        var xhr = this;
        if (mutations.get(xhr)) {
          xhr.addEventListener('loadend', function () {
            if (xhr.status >= 200 && xhr.status < 300) scheduler.schedule(null, 'cart-xhr');
          }, { once: true });
        }
        return originalSend.apply(this, arguments);
      };
    }

    function showCheckoutError(error) {
      if (typeof root.alert !== 'function') return;
      var fallback = error && error.message ? error.message : 'The subscription gift could not be updated.';
      root.alert((root.MinimogStrings && root.MinimogStrings.cartError) || fallback);
    }

    function replayClick(target, container) {
      var replacement = target && target.matches && target.matches('.cd-checkout-section-button')
        ? (container && container.querySelector && container.querySelector('.cd-checkout-section-button')) ||
          root.document.querySelector('.cd-checkout-section-button')
        : target;

      if (!replacement || !replacement.isConnected) {
        showCheckoutError(new Error('The checkout control changed while the subscription gift was updating.'));
        return;
      }

      checkoutReplay = true;
      replacement.click();
      checkoutReplay = false;
    }

    function observeCheckout() {
      if (!root.document) return;

      root.document.addEventListener('click', function (event) {
        if (checkoutReplay || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        var target = checkoutTarget(event);
        if (!target) return;
        var container = typeof target.getRootNode === 'function' ? target.getRootNode() : root.document;

        event.preventDefault();
        event.stopImmediatePropagation();
        scheduler.schedule(null, 'checkout').then(function (result) {
          if (result && result.ok) replayClick(target, container);
          else showCheckoutError(result && result.error);
        });
      }, true);

      root.document.addEventListener('submit', function (event) {
        if (checkoutReplay || event.defaultPrevented) return;
        var submitter = event.submitter;
        var form = event.target;
        var isCheckout = submitter && submitter.name === 'checkout';
        if (!isCheckout && form && form.action) isCheckout = /\/checkout(?:[?#]|$)/.test(form.action);
        if (!isCheckout) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        scheduler.schedule(null, 'checkout').then(function (result) {
          if (!result || !result.ok) {
            showCheckoutError(result && result.error);
            return;
          }
          if (!form.isConnected) {
            form = Array.from(root.document.forms).find(function (candidate) {
              return candidate.action === form.action;
            });
            submitter = form && form.querySelector('[name="checkout"]');
          }
          if (!form) {
            showCheckoutError(new Error('The checkout form changed while the subscription gift was updating.'));
            return;
          }
          checkoutReplay = true;
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
          else form.submit();
          checkoutReplay = false;
        });
      }, true);
    }

    function start() {
      scheduler.schedule(null, 'initial');
      if (config.enabled !== true) return;
      observeFetch();
      observeXhr();
      observeCartEvents();
      observeCheckout();
    }

    return {
      start: start,
      schedule: scheduler.schedule,
      transport: transport
    };
  }

  function init() {
    var config = root.OotbSubscriptionCartConfig;
    if (!config || typeof root.fetch !== 'function') return null;

    var runtime = createRuntime(config);
    runtime.start();
    return runtime;
  }

  return {
    GIFT_PROPERTY: GIFT_PROPERTY,
    GIFT_PROPERTY_VALUE: GIFT_PROPERTY_VALUE,
    createGiftItems: createGiftItems,
    createRuntime: createRuntime,
    createSerialScheduler: createSerialScheduler,
    desiredGiftIds: desiredGiftIds,
    executeReconciliation: executeReconciliation,
    init: init,
    isAnyGift: isAnyGift,
    isCartMutation: isCartMutation,
    isEligibleSubscription: isEligibleSubscription,
    isOurGift: isOurGift,
    planReconciliation: planReconciliation,
    sellingPlanId: sellingPlanId
  };
});
